/* Master Order → Расчёты → Пересчитать рецепт (self-contained).
 * Client-side parser. No backend. Persists nothing.
 * 4 phases: paste → review → resize → result.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Circle, Square, RectangleHorizontal, Copy, Plus, X } from "lucide-react";
import MOShell from "@/components/masterOrder/MOShell";
import RecipeCostDrawer from "@/components/masterOrder/RecipeCostDrawer";
import { createWorkbookEntry, updateWorkbookEntry, duplicateWorkbookEntry, listWorkbookEntries, getWorkbookEntry } from "@/lib/workbookApi";

/* Small helper: format the picker card's "Обновлено …" line (DD.MM.YYYY). */
const fmtEntryDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  } catch { return "—"; }
};

/* ─────────── parser: line → {name, amount, unit} ───────────── */
const UNIT_MAP = {
  // mass
  "г": "g", "гр": "g", "грам": "g", "грамм": "g", "граммов": "g", "g": "g",
  "кг": "kg", "килограмм": "kg", "kg": "kg",
  // volume
  "мл": "ml", "миллилитр": "ml", "ml": "ml",
  "л": "l", "литр": "l", "l": "l",
  "ст.л": "tbsp", "стл": "tbsp", "столовая": "tbsp", "tbsp": "tbsp",
  "ч.л": "tsp", "чл": "tsp", "чайная": "tsp", "tsp": "tsp",
  "стакан": "cup", "cup": "cup",
  // count
  "шт": "pcs", "штук": "pcs", "штуки": "pcs", "штука": "pcs", "pcs": "pcs",
  "щепотка": "pinch", "щепотки": "pinch",
  "по вкусу": "taste",
};
/* Recognised food units — a stricter whitelist we use to accept a parsed
   line as an ingredient. Anything else (time, temperature, prose) is
   considered technology text and stays out of Recipe Cost. */
const FOOD_UNIT_SET = new Set([
  "g", "kg", "ml", "l", "tbsp", "tsp", "cup", "pcs", "pinch", "taste",
]);
const TIME_TEMP_UNIT_RE = /^(°?c|°|градус\w*|мин\w*|сек\w*|час\w*|ч\.?|м\.?|s|мс|days?|дн\w*|hour\w*|second\w*|minute\w*)$/i;
const TECH_VERB_RE = /^(выпек\w*|выпеч\w*|печь|пропеч\w*|подпеч\w*|заморозить|замораж\w*|охлад\w*|остуд\w*|остыв\w*|убер\w*|убир\w*|остав\w*|поставить|постав\w*|перемеш\w*|взбить|взбива\w*|прогре\w*|дов[её]сти|довод\w*|варить|вар\w*|тушить|туш\w*|жарить|жар\w*|готов\w*|смеша\w*|доба[вв]\w*|снять|снима\w*|процед\w*|зали[тв]\w*|нагре\w*|разогр\w*|раздел\w*|раскат\w*|формов\w*|сформ\w*|темпер\w*|дать|давать|нанест\w*|нанос\w*|отсадить|отсаж\w*|стабилизи\w*|глазир\w*|карамелиз\w*|бланшир\w*|конден\w*)/i;
const RE_LINE = /^\s*[-–—•*·]?\s*([A-Za-zА-Яа-яЁё][A-Za-zА-Яа-яЁё\s'’()\-\/]{1,80}?)\s*[—–\-:]?\s*(\d+(?:[.,]\d+)?)\s*([A-Za-zА-Яа-яЁё.]{0,10})\.?\s*$/;

/* Amount-first patterns: «250 г муки», «3 яйца», «1 ч. л. ванильного экстракта».
 * We recognise a longest known unit prefix right after the number; if none is
 * found we fall back to `pcs` (implicit count for «3 яйца») unless the first
 * word looks like a time / technique token — in which case the line is left
 * for the technology block. */
const UNIT_PREFIXES = [
  ["столовых ложек", "ст.л"], ["столовые ложки", "ст.л"], ["столовая ложка", "ст.л"],
  ["чайных ложек", "ч.л"], ["чайные ложки", "ч.л"], ["чайная ложка", "ч.л"],
  ["миллилитр", "мл"], ["килограмм", "кг"], ["граммов", "гр"], ["грамм", "гр"],
  ["стаканов", "стакан"], ["стакана", "стакан"], ["стакан", "стакан"],
  ["штуки", "шт"], ["штука", "шт"], ["штук", "шт"], ["литр", "л"],
  ["ст. л.", "ст.л"], ["ст.л.", "ст.л"], ["ст. л", "ст.л"], ["ст.л", "ст.л"],
  ["ч. л.", "ч.л"], ["ч.л.", "ч.л"], ["ч. л", "ч.л"], ["ч.л", "ч.л"],
  ["tbsp", "tbsp"], ["tsp", "tsp"], ["cup", "cup"],
  ["кг", "кг"], ["мл", "мл"], ["гр", "гр"], ["шт", "шт"],
  ["kg", "kg"], ["ml", "ml"], ["pcs", "pcs"],
  ["г", "г"], ["л", "л"], ["g", "g"], ["l", "l"],
].sort((a, b) => b[0].length - a[0].length);

const matchLongestUnit = (rest) => {
  const lower = rest.toLowerCase();
  for (const [tok, canonical] of UNIT_PREFIXES) {
    if (lower.startsWith(tok)) {
      const next = rest[tok.length];
      // Unit must be followed by a space (or end of line) so we don't eat
      // the first letter of an ingredient name (e.g. «г» inside «горького»).
      if (next === undefined || next === " " || next === "\t") {
        return { canonical, length: tok.length };
      }
    }
  }
  return null;
};

const tryAmountFirst = (line) => {
  const m = line.match(/^\s*[-–—•*·]?\s*(\d+(?:[.,]\d+)?)\s+(.+)$/);
  if (!m) return null;
  const amount = Number(m[1].replace(",", "."));
  const rest = m[2].trim();
  if (!rest) return null;
  const unitMatch = matchLongestUnit(rest);
  let unitRaw, name;
  if (unitMatch) {
    unitRaw = unitMatch.canonical;
    name = rest.slice(unitMatch.length).trim();
  } else {
    // No unit token → implicit count. Bail out for time / technique tokens
    // so «30 минут» / «5 секунд» / «заморозки 2 часа» stay out of Recipe Cost.
    const firstWord = rest.split(/\s+/)[0].toLowerCase().replace(/\.$/, "");
    if (TIME_TEMP_UNIT_RE.test(firstWord) || TECH_VERB_RE.test(firstWord)) return null;
    unitRaw = "";
    name = rest;
  }
  if (!name || !Number.isFinite(amount) || amount <= 0) return null;
  return { name: name.replace(/\s{2,}/g, " "), amount, unitRaw };
};

const WORD_QTY_MAP = {
  "щепотка": { amount: 1, unitRaw: "щепотка" },
  "щепотки": { amount: 1, unitRaw: "щепотка" },
  "щепотку": { amount: 1, unitRaw: "щепотка" },
};

const tryWordQty = (line) => {
  const cleaned = line.replace(/^\s*[-–—•*·]?\s*/, "");
  const lower = cleaned.toLowerCase();
  for (const key of Object.keys(WORD_QTY_MAP)) {
    if (lower.startsWith(key)) {
      const next = cleaned[key.length];
      if (next === undefined || next === " ") {
        const name = cleaned.slice(key.length).trim();
        if (name) return { name: name.replace(/\s{2,}/g, " "), ...WORD_QTY_MAP[key] };
      }
    }
  }
  return null;
};

const parseRecipe = (raw) => {
  const lines = String(raw).split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const items = [];
  const technique = [];
  let title = "";

  const pushIngredient = (name, amount, unitRaw) => {
    const key = (unitRaw || "").toLowerCase().replace(/\./g, "");
    const mappedUnit = UNIT_MAP[key] || key || "pcs";
    const looksTech = TECH_VERB_RE.test(name)
      || TIME_TEMP_UNIT_RE.test(key)
      || (!FOOD_UNIT_SET.has(mappedUnit) && key !== "");
    if (looksTech) return false;
    if (name.length < 2 || !Number.isFinite(amount) || amount <= 0) return false;
    items.push({ name, amount, unit: mappedUnit });
    return true;
  };

  for (const l of lines) {
    if (!title && /^[а-яa-zё\s"'«»()!?\-.]+$/i.test(l) && !RE_LINE.test(l) && !tryWordQty(l) && l.length < 80) {
      title = l; continue;
    }
    // 1. existing «мука 250 г» pattern
    const m = l.match(RE_LINE);
    if (m) {
      const rawName = m[1].trim().replace(/\s{2,}/g, " ");
      const amount = Number(m[2].replace(",", "."));
      const unitRaw = (m[3] || "").toLowerCase().replace(/\./g, "");
      if (pushIngredient(rawName, amount, unitRaw)) continue;
      // Fall through if the line matched but is technology (e.g. «заморозить 30 минут»)
      // — pushed to technique below.
      technique.push(l);
      continue;
    }
    // 2. «250 г муки» / «3 яйца» / «1 ч. л. ванильного экстракта»
    const alt = tryAmountFirst(l);
    if (alt) {
      if (pushIngredient(alt.name, alt.amount, alt.unitRaw)) continue;
      technique.push(l);
      continue;
    }
    // 3. «щепотка соли»
    const wq = tryWordQty(l);
    if (wq) {
      if (pushIngredient(wq.name, wq.amount, wq.unitRaw)) continue;
      technique.push(l);
      continue;
    }
    // 4. Fallback → technology text.
    technique.push(l);
  }
  return { title: title || "Рецепт", items, technique: technique.join("\n") };
};

/* ─────────── geometry ───────────── */
const shapeArea = (s) => {
  if (s.shape === "round") return Math.PI * (Number(s.diameter) / 2) ** 2;
  if (s.shape === "square") return Number(s.side) ** 2;
  if (s.shape === "rect") return Number(s.length) * Number(s.width);
  return 0;
};
const shapeSummary = (s) => {
  const h = s.height ? ` · h ${s.height} см` : "";
  const n = s.count > 1 ? ` × ${s.count}` : "";
  if (s.shape === "round") return `Круг Ø${s.diameter} см${h}${n}`;
  if (s.shape === "square") return `Квадрат ${s.side}×${s.side} см${h}${n}`;
  return `Прямоугольник ${s.length}×${s.width} см${h}${n}`;
};

const emptyShape = () => ({ shape: "round", diameter: 20, side: 20, length: 25, width: 15, height: "", count: 1 });

/* ─────────── UI primitives ─────────── */
const Card = ({ children, testid }) => (
  <div className="rounded-xl bg-white p-4" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid={testid}>
    {children}
  </div>
);
const Label = ({ children }) => (
  <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1.5" style={{ color: "#8B6F4B" }}>{children}</div>
);
const NumInput = ({ value, onChange, placeholder, testid }) => (
  <input type="text" inputMode="decimal" value={value ?? ""}
         onChange={(e) => onChange(e.target.value.replace(",", "."))}
         placeholder={placeholder}
         className="w-full h-10 px-3 rounded-md bg-white text-base md:text-[13px] outline-none tabular-nums"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
         data-testid={testid} />
);
const PrimaryBtn = ({ onClick, disabled, testid, children }) => (
  <button onClick={onClick} disabled={disabled}
          className="h-11 px-5 rounded-full text-[13px] disabled:opacity-60"
          style={{ background: "#4A3728", color: "#F7EFE0" }}
          data-testid={testid}>{children}</button>
);
const GhostBtn = ({ onClick, testid, children }) => (
  <button onClick={onClick}
          className="h-11 px-4 rounded-full text-[13px]"
          style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
          data-testid={testid}>{children}</button>
);

const ShapePicker = ({ value, onChange, testidPrefix }) => (
  <div className="flex gap-1.5" data-testid={`${testidPrefix}-picker`}>
    {[
      { v: "round", label: "Круг", Icon: Circle },
      { v: "square", label: "Квадрат", Icon: Square },
      { v: "rect", label: "Прямоуг.", Icon: RectangleHorizontal },
    ].map(({ v, label, Icon }) => (
      <button key={v} onClick={() => onChange(v)}
              className="h-9 px-3 rounded-full text-[12px] inline-flex items-center gap-1.5"
              style={{
                background: value === v ? "#4A3728" : "#F7F1E7",
                color: value === v ? "#F7EFE0" : "#4A3728",
                boxShadow: value === v ? "none" : "inset 0 0 0 1px #EFE4D0",
              }}
              data-testid={`${testidPrefix}-${v}`}>
        <Icon size={12} /> {label}
      </button>
    ))}
  </div>
);

const ShapeCard = ({ title, value, setValue, testidPrefix }) => {
  const update = (k, v) => setValue({ ...value, [k]: v });
  return (
    <Card testid={`${testidPrefix}-card`}>
      <div className="text-[13px] mb-2" style={{ color: "#2A1D14" }}>{title}</div>
      <ShapePicker value={value.shape} onChange={(v) => update("shape", v)} testidPrefix={testidPrefix} />
      <div className="mt-3 grid grid-cols-2 gap-2">
        {value.shape === "round" && (
          <div><Label>Ø см</Label><NumInput value={value.diameter} onChange={(v) => update("diameter", v)} testid={`${testidPrefix}-diameter`} /></div>
        )}
        {value.shape === "square" && (
          <div><Label>Сторона см</Label><NumInput value={value.side} onChange={(v) => update("side", v)} testid={`${testidPrefix}-side`} /></div>
        )}
        {value.shape === "rect" && (<>
          <div><Label>Длина см</Label><NumInput value={value.length} onChange={(v) => update("length", v)} testid={`${testidPrefix}-length`} /></div>
          <div><Label>Ширина см</Label><NumInput value={value.width} onChange={(v) => update("width", v)} testid={`${testidPrefix}-width`} /></div>
        </>)}
        <div><Label>Высота см</Label><NumInput value={value.height} onChange={(v) => update("height", v)} placeholder="—" testid={`${testidPrefix}-height`} /></div>
        <div><Label>Форм, шт</Label><NumInput value={value.count} onChange={(v) => update("count", v)} testid={`${testidPrefix}-count`} /></div>
      </div>
    </Card>
  );
};

const fmtNum = (n, digits = 1) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const rounded = v >= 10 ? Math.round(v) : Number(v.toFixed(digits));
  return String(rounded).replace(".", ",");
};

/* ─── Practical rounding for pastry-chef ingredient amounts ────────────────
 * The math (area/volume ratio) can produce values like 82.7605 g of powdered
 * sugar or 1.2732 g of salt. Those are inconvenient to weigh.
 * We map the exact math result to a working amount whose deviation is
 * within ~3 % for normal ingredients, keeping precise / very-precise
 * ingredients closer to real dosing (0.1 g / 0.01 g resolution).
 *
 * Classification is by NAME (Russian keyword patterns), so callers must pass
 * the ingredient row, not just its number. Piece units are never rescaled —
 * the existing pcs / grams_per_piece logic elsewhere in the app owns that.
 * -------------------------------------------------------------------------- */
const RX_VERY_PRECISE = /(красител|пищев\w* краск|ароматизатор|эссенц|экстракт|эфирн\w* масл|концентрат)/i;
const RX_PRECISE = /(пектин|агар|желатин|разрыхлит|сод[ауыи]\b|\bсол[ьюия]|лимонн\w* кислот|кислот[аыуе]\b|ксантан|гуар|эмульгатор|лецитин|диоксид|дрожж)/i;

const classifyIngredient = (name, unit) => {
  if (unit === "pcs") return "piece";
  const s = String(name || "");
  if (RX_VERY_PRECISE.test(s)) return "very_precise";
  if (RX_PRECISE.test(s)) return "precise";
  return "normal";
};

const NORMAL_STEPS_G = [5, 2, 1, 0.5, 0.1]; // largest → smallest
const roundToStep = (v, step) => Math.round(v / step) * step;
const relErr = (exact, r) => (exact === 0 ? 0 : Math.abs(r - exact) / Math.abs(exact));
const stripFloat = (n, d) => parseFloat(Number(n).toFixed(d));

/* Structural pcs: currently limited to eggs. Berries / cookies / toppers /
 * decor stay as fractional pcs — chef's intent varies too much to auto-round
 * them to integers.  A recipe row is treated as "egg" only if its name
 * starts with яйц/яйца/яйцо (word-initial), so «яичный белок» is NOT matched. */
const isEggIngredient = (name) => /^яйц[оеаиуы]?(\s|,|$)/i.test(String(name || "").trim());

const workingAmountFor = (exactRaw, name, unit) => {
  const exact = Number(exactRaw);
  if (!Number.isFinite(exact)) return exactRaw;
  const cls = classifyIngredient(name, unit);
  if (cls === "piece") return exact; // pcs untouched

  const applyGramsClass = (grams) => {
    if (cls === "normal") {
      for (const step of NORMAL_STEPS_G) {
        const r = roundToStep(grams, step);
        if (r > 0 && relErr(grams, r) <= 0.03) return stripFloat(r, 3);
      }
      return stripFloat(grams, 1);
    }
    if (cls === "precise") return stripFloat(grams, 1);
    // very_precise
    return stripFloat(grams, 2);
  };

  if (unit === "g" || unit === "ml") return applyGramsClass(exact);
  if (unit === "kg" || unit === "l") {
    // Apply the same ladder in grams-scale and convert back so 0.083529 kg → 0.085 kg
    const small = applyGramsClass(exact * 1000);
    return stripFloat(small / 1000, 4);
  }
  // tbsp / tsp / cup / pinch / taste and everything else — 2 decimals, no ladder.
  return stripFloat(exact, 2);
};


/* ─────────── main page ─────────── */
export default function MORecipeRecalc() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Sprint MO-UX-Simplify: when opened from an Order position, we surface
  // a «К заказу №N» chip so the confectioner never loses their order.
  const orderCtxId = searchParams.get("order_id");
  const [orderCtxNumber, setOrderCtxNumber] = useState("");
  useEffect(() => {
    if (!orderCtxId) return;
    (async () => {
      try {
        const o = await (await import("@/lib/masterOrderApi")).getOrder(orderCtxId);
        const n = String(o?.order_number || "").replace(/^0+/, "") || "0";
        setOrderCtxNumber(n);
      } catch { /* silent */ }
    })();
  }, [orderCtxId]);
  const [phase, setPhase] = useState("paste"); // paste | review | resize | result
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [recipe, setRecipe] = useState({ title: "", items: [], technique: "" });
  const [fromShape, setFromShape] = useState(emptyShape());
  const [toShape, setToShape] = useState(emptyShape());
  const [toHeightTouched, setToHeightTouched] = useState(false);
  const [result, setResult] = useState(null);
  const [costOpen, setCostOpen] = useState(false);
  const [costItems, setCostItems] = useState([]);
  const [costSnapshot, setCostSnapshot] = useState(null);

  // Workbook — «Рабочая тетрадь» integration
  const [savedEntryId, setSavedEntryId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const markDirty = () => { if (savedEntryId) setDirty(true); };

  // Picker: «Выбрать из моих рецептов» — reads existing workbook entries only,
  // never mutates them, never auto-links the loaded recipe to its source entry.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerEntries, setPickerEntries] = useState([]);
  const [pickerBusyId, setPickerBusyId] = useState(null);

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const rows = await listWorkbookEntries({ type: "recipe_calculation" });
      setPickerEntries(rows || []);
    } catch (e) {
      toast.error("Не удалось загрузить рецепты");
      setPickerEntries([]);
    } finally { setPickerLoading(false); }
  };

  // Display-side normalization ONLY at the Workbook → Recipe Recalc boundary.
  // The stored payload keeps full precision (Decimal-as-string). We round for
  // the review form so pastry chefs don't see 82.7605263157895 g after opening
  // a saved recalculated variant. Rules per user spec:
  //   • g, ml    → 1 fractional digit
  //   • kg, l    → 3 fractional digits (preserves 0.083 kg = 83 g fidelity)
  //   • pcs      → integer if effectively whole, else up to 2 digits
  //   • all rest → up to 2 fractional digits
  // Trailing zeros are stripped (82.0 → 82; 216.50 → 216.5) via parseFloat.
  // Anything unparseable falls through untouched.
  const AMOUNT_DIGITS_BY_UNIT = { g: 1, ml: 1, kg: 3, l: 3, pcs: 2, tbsp: 2, tsp: 2, cup: 2 };
  const normalizeAmountForReview = (raw, unit) => {
    if (raw === "" || raw === null || raw === undefined) return "";
    const n = Number(String(raw).replace(",", "."));
    if (!Number.isFinite(n)) return raw;
    const d = AMOUNT_DIGITS_BY_UNIT[unit] ?? 2;
    const rounded = parseFloat(n.toFixed(d));
    if (unit === "pcs" && Math.abs(rounded - Math.round(rounded)) < 1e-9) return Math.round(rounded);
    return rounded;
  };

  const normalizeIngredient = (row) => {
    const unit = row?.unit || "g";
    return {
      name: String(row?.name || "").trim(),
      amount: normalizeAmountForReview(row?.amount, unit),
      unit,
    };
  };

  const pickEntry = async (id) => {
    if (pickerBusyId) return;
    setPickerBusyId(id);
    try {
      const entry = await getWorkbookEntry(id);
      const p = entry?.payload || {};
      // Prefer the recalculated variant if present, else the source recipe.
      const rawItems = (Array.isArray(p.recalculated_ingredients) && p.recalculated_ingredients.length > 0)
        ? p.recalculated_ingredients
        : (p.source_ingredients || []);
      const items = rawItems.map(normalizeIngredient).filter((r) => r.name);
      if (items.length === 0) {
        toast.error("В записи нет ингредиентов");
        setPickerBusyId(null);
        return;
      }
      // Reset the whole calc state so the flow starts fresh from "review".
      setRecipe({
        title: String(p.title || entry.title || "").trim() || "Рецепт",
        items,
        technique: p.technique || "",
      });
      setFromShape(emptyShape());
      setToShape(emptyShape());
      setToHeightTouched(false);
      setResult(null);
      setCostSnapshot(null);
      // No auto-link: a new save (via existing action) creates a NEW entry.
      // Explicit user actions «Сохранить изменения» / «Сохранить как новый вариант»
      // remain the only ways to write to the workbook.
      setSavedEntryId(null);
      setDirty(false);
      setPickerOpen(false);
      setPhase("review");
    } catch (e) {
      toast.error("Не удалось открыть рецепт");
    } finally { setPickerBusyId(null); }
  };

  /* Height-mirroring: by default target height follows source height,
     so changing shape/size scales ingredients by area only.
     If user explicitly edits target height, they take over. */
  const handleFromShape = (next) => {
    const heightChanged = String(next.height ?? "") !== String(fromShape.height ?? "");
    setFromShape(next);
    if (heightChanged && !toHeightTouched) {
      setToShape((prev) => ({ ...prev, height: next.height }));
    }
  };
  const handleToShape = (next) => {
    if (String(next.height ?? "") !== String(toShape.height ?? "")) {
      setToHeightTouched(true);
    }
    setToShape(next);
  };

  const doParse = () => {
    if (!pasteText.trim()) { toast.error("Вставьте текст рецепта"); return; }
    if (pasteText.length > 5000) { toast.error("Слишком длинный текст (лимит 5000 символов)"); return; }
    const parsed = parseRecipe(pasteText);
    if (parsed.items.length === 0) { toast.error("Не удалось распознать ингредиенты. Проверьте формат: «мука 250 г»"); return; }
    setRecipe(parsed);
    setPasteOpen(false);
    setPhase("review");
  };

  const doConfirm = () => {
    if (recipe.items.length === 0) { toast.error("Нужен хотя бы один ингредиент"); return; }
    setPhase("choice");
  };

  const openCostFromChoice = () => {
    setCostItems(recipe.items);
    setCostOpen(true);
  };
  const openCostFromResult = () => {
    setCostItems(result?.items || []);
    setCostOpen(true);
  };

  const factor = useMemo(() => {
    const a1 = shapeArea(fromShape); const a2 = shapeArea(toShape);
    if (!a1 || !a2) return null;
    const h1 = Number(fromShape.height) || 0;
    const h2 = Number(toShape.height) || 0;
    const useHeight = h1 > 0 && h2 > 0;
    const v1 = useHeight ? a1 * h1 * (Number(fromShape.count) || 1) : a1 * (Number(fromShape.count) || 1);
    const v2 = useHeight ? a2 * h2 * (Number(toShape.count) || 1) : a2 * (Number(toShape.count) || 1);
    return { value: v2 / v1, useHeight };
  }, [fromShape, toShape]);

  const doRecalc = () => {
    if (!factor || !Number.isFinite(factor.value) || factor.value <= 0) {
      toast.error("Проверьте размеры форм"); return;
    }
    const geometryCoefficient = factor.value;

    // ── Structural pcs renormalization for eggs ───────────────────────────
    // If ANY row of the confirmed recipe is «яйцо/яйца» in pcs, we compute an
    // integer working amount for eggs first, then derive a *working
    // coefficient* from egg ratio and apply it to every other ingredient so
    // proportions stay correct. Eggs given in grams stay in grams (never
    // enter this branch — the unit === "pcs" guard filters them out).
    const eggIdxs = [];
    let sourceEggsTotal = 0;
    recipe.items.forEach((it, idx) => {
      if (it.unit === "pcs" && isEggIngredient(it.name)) {
        eggIdxs.push(idx);
        sourceEggsTotal += Number(it.amount) || 0;
      }
    });

    let workingCoefficient = geometryCoefficient;
    let workingEggsTotal = null;
    let renormNote = null;
    if (eggIdxs.length > 0 && sourceEggsTotal > 0) {
      const exactEggsTotal = sourceEggsTotal * geometryCoefficient;
      workingEggsTotal = Math.max(1, Math.round(exactEggsTotal));
      // Guard against div-by-zero (unreachable given the > 0 check above).
      workingCoefficient = workingEggsTotal / sourceEggsTotal;
      renormNote = `Расчёт скорректирован под ${workingEggsTotal} ${workingEggsTotal === 1 ? "яйцо" : (workingEggsTotal < 5 ? "яйца" : "яиц")} для сохранения пропорций.`;
    }

    const newItems = recipe.items.map((it, idx) => {
      // Egg rows: distribute integer working total in proportion to their
      // source shares so a two-row egg recipe («желток 2 pcs», «яйцо 3 pcs»)
      // still sums up right. Single-row case = full total in that row.
      if (eggIdxs.includes(idx) && workingEggsTotal !== null) {
        const share = sourceEggsTotal > 0
          ? Math.max(1, Math.round((Number(it.amount) || 0) / sourceEggsTotal * workingEggsTotal))
          : workingEggsTotal;
        return { ...it, amount: share };
      }
      const exact = Number(it.amount) * workingCoefficient;
      const working = workingAmountFor(exact, it.name, it.unit);
      return { ...it, amount: working };
    });

    const totalGrams = newItems
      .filter((it) => it.unit === "g")
      .reduce((s, it) => s + Number(it.amount || 0), 0)
      + newItems.filter((it) => it.unit === "kg").reduce((s, it) => s + Number(it.amount || 0) * 1000, 0);
    setResult({
      items: newItems,
      factor: workingCoefficient,
      geometryCoefficient,
      useHeight: factor.useHeight,
      totalGrams,
      renormNote,
    });
    setPhase("result");
  };

  const copyResult = () => {
    if (!result) return;
    const lines = [
      `${recipe.title} — пересчитано ×${fmtNum(result.factor, 3)}`,
      `Форма: ${shapeSummary(toShape)}`,
      "",
      ...result.items.map((it) => `${it.name} — ${fmtNum(it.amount)} ${it.unit}`),
    ].join("\n");
    navigator.clipboard?.writeText(lines);
    toast.success("Скопировано");
  };

  /* ────────────────────── Workbook «Рабочая тетрадь» ────────────────────── */
  const dec = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? String(n) : null;
  };
  const shapeToPayload = (s) => {
    const out = {};
    if (s?.shape) out.shape = s.shape;
    for (const k of ["diameter", "side", "length", "width", "height", "count"]) {
      const v = dec(s?.[k]);
      if (v !== null) out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  };
  const ingredientsToPayload = (arr) =>
    (arr || [])
      .filter((it) => (it?.name || "").trim())
      .map((it) => {
        const amount = dec(it.amount);
        const row = { name: it.name.trim() };
        if (amount !== null) row.amount = amount;
        if (it.unit) row.unit = it.unit;
        return row;
      });

  const buildWorkbookPayload = () => {
    // If the user is on the cost-only path (no recalculation happened),
    // do NOT persist mold geometry — target_shape would otherwise be a
    // synthetic snapshot of the empty default form.
    const hasResult = !!result;
    return {
      title: (recipe.title || "Рецепт").trim(),
      source_ingredients: ingredientsToPayload(recipe.items),
      technique: recipe.technique || null,
      recalculated_ingredients: hasResult ? ingredientsToPayload(result?.items || []) : [],
      source_shape: hasResult ? shapeToPayload(fromShape) : null,
      target_shape: hasResult ? shapeToPayload(toShape) : null,
      cost: costSnapshot || null,
      currency_code: costSnapshot?.currency_code || "EUR",
    };
  };

  const saveToWorkbook = async () => {
    setSaving(true);
    try {
      const created = await createWorkbookEntry({
        type: "recipe_calculation",
        payload: buildWorkbookPayload(),
      });
      setSavedEntryId(created.id);
      setDirty(false);
      toast.success("Сохранено в Рабочую тетрадь");
    } catch (e) {
      toast.error("Не удалось сохранить");
    } finally { setSaving(false); }
  };

  const saveChanges = async () => {
    if (!savedEntryId) return saveToWorkbook();
    setSaving(true);
    try {
      await updateWorkbookEntry(savedEntryId, { payload: buildWorkbookPayload() });
      setDirty(false);
      toast.success("Изменения сохранены");
    } catch (e) {
      toast.error("Не удалось сохранить изменения");
    } finally { setSaving(false); }
  };

  const saveAsVariant = async () => {
    if (!savedEntryId) return;
    setSaving(true);
    try {
      const variant = await duplicateWorkbookEntry(savedEntryId, {
        payload: buildWorkbookPayload(),
      });
      setSavedEntryId(variant.id);
      setDirty(false);
      toast.success("Сохранено как новый вариант");
    } catch (e) {
      toast.error("Не удалось сохранить вариант");
    } finally { setSaving(false); }
  };

  return (
    <MOShell active="recipe-recalc" title="Пересчитать рецепт" back="/master-order">
      <div className="max-w-3xl mx-auto px-4 md:px-6 pb-16 pt-4" data-testid="mo-recipe-recalc">
        {orderCtxId && (
          <div className="mb-4 flex items-center justify-between gap-2 rounded-lg px-3 py-2"
               style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-recipe-recalc-order-context">
            <div className="text-[12.5px]" style={{ color: "#5B4326" }}>
              Открыто из заказа № {orderCtxNumber || "…"}
            </div>
            <button onClick={() => navigate(`/master-order/orders/${orderCtxId}`)}
                    className="h-8 px-3 rounded-full text-[12px]"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-recipe-recalc-back-to-order">
              К заказу
            </button>
          </div>
        )}

        {/* ══════════ PHASE 1: PASTE ══════════ */}
        {phase === "paste" && (
          <div>
            <div className="text-center mb-6">
              <div className="text-[24px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                Новый расчёт
              </div>
              <div className="text-[13px] mt-1" style={{ color: "#8B6F4B" }}>
                Начните с рецепта — база ингредиентов не нужна.
              </div>
            </div>
            <div className="grid gap-2">
              <button onClick={() => setPasteOpen(true)}
                      className="rounded-xl bg-white p-4 text-left"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-paste-btn">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Вставить рецепт</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>Скопируйте текст из любого источника — до 5 000 символов.</div>
              </button>
              <button onClick={openPicker}
                      className="rounded-xl bg-white p-4 text-left"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-choose-btn">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Выбрать из моих рецептов</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>Открыть сохранённый рецепт из Рабочей тетради.</div>
              </button>
            </div>
          </div>
        )}

        {/* Compact paste modal */}
        {pasteOpen && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
               style={{ background: "rgba(35,23,16,0.5)" }}
               onClick={(e) => { if (e.target === e.currentTarget) setPasteOpen(false); }}
               data-testid="mo-recalc-paste-modal">
            <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
                 style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
              <div className="flex-shrink-0 p-5 md:p-6 pb-2">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Вставьте рецепт</div>
                  <button onClick={() => setPasteOpen(false)}
                          className="w-8 h-8 rounded-full grid place-items-center"
                          style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          aria-label="Закрыть">
                    <X size={13} />
                  </button>
                </div>
                <div className="text-[12px]" style={{ color: "#8B6F4B" }}>
                  Каждая строка — «название — количество единица». Например: <i>мука — 250 г</i>.
                </div>
              </div>
              <div className="flex-1 min-h-0 px-5 md:px-6 overflow-y-auto">
                <textarea value={pasteText}
                          onChange={(e) => setPasteText(e.target.value.slice(0, 5000))}
                          rows={8} placeholder="Название рецепта&#10;мука 250 г&#10;яйца 3 шт&#10;сахар 200 г"
                          className="w-full p-3 rounded-lg bg-white text-base md:text-[13px] outline-none resize-none"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14", minHeight: "160px" }}
                          data-testid="mo-recalc-paste-text" />
              </div>
              <div className="flex-shrink-0 flex items-center justify-between p-5 md:p-6 pt-3"
                   style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
                <div className="text-[11px]" style={{ color: "#B9A692" }}>{pasteText.length}/5000</div>
                <div className="flex gap-2">
                  <GhostBtn onClick={() => setPasteOpen(false)} testid="mo-recalc-paste-cancel">Отмена</GhostBtn>
                  <PrimaryBtn onClick={doParse} testid="mo-recalc-parse">Распознать рецепт</PrimaryBtn>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Picker modal: «Выбрать из моих рецептов» — read-only listing of
            existing Workbook entries. Selecting an entry loads it into the
            existing review flow WITHOUT creating a copy or linking to source. */}
        {pickerOpen && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
               style={{ background: "rgba(35,23,16,0.5)" }}
               onClick={(e) => { if (e.target === e.currentTarget) setPickerOpen(false); }}
               data-testid="mo-recalc-picker-modal">
            <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
                 style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
              <div className="flex-shrink-0 p-5 md:p-6 pb-2">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                    Выбрать из моих рецептов
                  </div>
                  <button onClick={() => setPickerOpen(false)}
                          className="w-8 h-8 rounded-full grid place-items-center"
                          style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          aria-label="Закрыть"
                          data-testid="mo-recalc-picker-close">
                    <X size={13} />
                  </button>
                </div>
                <div className="text-[12px]" style={{ color: "#8B6F4B" }}>
                  Сохранённые в Рабочей тетради рецепты.
                </div>
              </div>
              <div className="flex-1 min-h-0 px-5 md:px-6 pb-4 overflow-y-auto">
                {pickerLoading && (
                  <div className="text-[13px] py-4" style={{ color: "#8B6F4B" }}>Загружаем…</div>
                )}
                {!pickerLoading && pickerEntries.length === 0 && (
                  <div className="rounded-lg p-3 text-center"
                       style={{ background: "#F7F1E7", color: "#5B4326",
                                boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                       data-testid="mo-recalc-picker-empty">
                    <div className="text-[13.5px]">В Рабочей тетради пока нет сохранённых рецептов</div>
                    <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                      Вставьте первый рецепт и сохраните его — он появится здесь.
                    </div>
                  </div>
                )}
                {!pickerLoading && pickerEntries.length > 0 && (
                  <div className="space-y-1.5" data-testid="mo-recalc-picker-list">
                    {pickerEntries.map((e) => (
                      <button key={e.id}
                              onClick={() => pickEntry(e.id)}
                              disabled={pickerBusyId === e.id}
                              className="w-full text-left rounded-lg p-3 bg-white disabled:opacity-60"
                              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                              data-testid={`mo-recalc-picker-item-${e.id}`}>
                        <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}>
                          {e.title || "Рецепт"}
                        </div>
                        <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                          Обновлено {fmtEntryDate(e.updated_at)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}



        {/* ══════════ PHASE 2: REVIEW ══════════ */}
        {phase === "review" && (
          <div>
            <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>Проверьте рецепт</div>
            <Card testid="mo-recalc-review">
              <Label>Название</Label>
              <input value={recipe.title}
                     onChange={(e) => setRecipe({ ...recipe, title: e.target.value })}
                     className="w-full h-10 px-3 rounded-md bg-white text-base md:text-[13.5px] outline-none mb-3"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-recalc-title" />
              <Label>Ингредиенты</Label>
              <div className="space-y-1.5">
                {recipe.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_64px_56px_26px] gap-1 items-center"
                       data-testid="mo-recalc-review-row">
                    <input value={it.name}
                           onChange={(e) => {
                             const val = e.target.value;
                             setRecipe((prev) => ({
                               ...prev,
                               items: prev.items.map((row, idx) => idx === i ? { ...row, name: val } : row),
                             }));
                           }}
                           className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none min-w-0"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                    <input type="text" inputMode="decimal" value={it.amount}
                           onChange={(e) => {
                             const parsed = Number(String(e.target.value).replace(",", ".")) || 0;
                             setRecipe((prev) => ({
                               ...prev,
                               items: prev.items.map((row, idx) => idx === i ? { ...row, amount: parsed } : row),
                             }));
                           }}
                           className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none text-right tabular-nums min-w-0"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                    <select value={it.unit}
                            onChange={(e) => {
                              const nextUnit = e.target.value;
                              setRecipe((prev) => ({
                                ...prev,
                                items: prev.items.map((row, idx) => idx === i ? { ...row, unit: nextUnit } : row),
                              }));
                            }}
                            className="h-9 px-1 rounded-md bg-white text-base md:text-[12px] outline-none min-w-0"
                            style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
                      {["g","kg","ml","l","tbsp","tsp","cup","pcs","pinch","taste"].map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <button onClick={() =>
                              setRecipe((prev) => ({ ...prev, items: prev.items.filter((_, j) => j !== i) }))}
                            aria-label="Удалить"
                            className="w-[26px] h-[26px] rounded-full grid place-items-center justify-self-end"
                            style={{ background: "#F7F1E7", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={() => setRecipe({ ...recipe, items: [...recipe.items, { name: "", amount: 0, unit: "g" }] })}
                      className="mt-2 h-8 px-3 rounded-full text-[12px] inline-flex items-center gap-1"
                      style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-add-line">
                <Plus size={11} /> Добавить строку
              </button>
            </Card>

            <div className="mt-3">
              <Card testid="mo-recalc-technique">
                <Label>Технология приготовления</Label>
                <textarea value={recipe.technique || ""}
                          onChange={(e) => setRecipe({ ...recipe, technique: e.target.value.slice(0, 2000) })}
                          rows={3}
                          placeholder="Заморозить 30 минут, выпекать при 170°C…"
                          className="w-full p-2 rounded-md bg-white text-base md:text-[13px] outline-none resize-y"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14", minHeight: "68px" }}
                          data-testid="mo-recalc-technique-text" />
                <div className="mt-1 text-[11px]" style={{ color: "#B9A692" }}>
                  Не участвует в расчёте себестоимости — сохраняется отдельно.
                </div>
              </Card>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <GhostBtn onClick={() => setPhase("paste")} testid="mo-recalc-back-to-paste">Назад</GhostBtn>
              <PrimaryBtn onClick={doConfirm} testid="mo-recalc-confirm">Подтвердить</PrimaryBtn>
            </div>
          </div>
        )}

        {/* ══════════ PHASE 2.5: CHOICE ══════════ */}
        {phase === "choice" && (
          <div data-testid="mo-recalc-choice">
            <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
              Что дальше
            </div>
            <div className="grid gap-2">
              <button onClick={() => setPhase("resize")}
                      className="rounded-xl bg-white p-4 text-left"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-choice-resize">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Пересчитать форму</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                  Изменить диаметр, высоту или количество форм — ингредиенты пересчитаются автоматически.
                </div>
              </button>
              <button onClick={openCostFromChoice}
                      className="rounded-xl bg-white p-4 text-left"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-choice-cost">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Посчитать себестоимость</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                  По исходным количествам рецепта — цены сохранятся и подтянутся в следующих расчётах.
                </div>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <GhostBtn onClick={() => setPhase("review")} testid="mo-recalc-choice-back">Назад</GhostBtn>
            </div>
          </div>
        )}

        {/* ══════════ PHASE 3: RESIZE ══════════ */}
        {phase === "resize" && (
          <div>
            <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>Пересчитать форму</div>
            <div className="grid md:grid-cols-2 gap-3">
              <ShapeCard title="Форма в рецепте" value={fromShape} setValue={handleFromShape} testidPrefix="mo-recalc-from" />
              <ShapeCard title="Ваша форма" value={toShape} setValue={handleToShape} testidPrefix="mo-recalc-to" />
            </div>
            {factor && (
              <div className="mt-3 text-[12px]" style={{ color: "#8B6F4B" }} data-testid="mo-recalc-factor-preview">
                Коэффициент: ×{fmtNum(factor.value, 3)}
                {!factor.useHeight && " · без учёта высоты"}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <GhostBtn onClick={() => setPhase("choice")} testid="mo-recalc-back-to-review">Назад</GhostBtn>
              <PrimaryBtn onClick={doRecalc} testid="mo-recalc-recalc">Пересчитать</PrimaryBtn>
            </div>
          </div>
        )}

        {/* ══════════ PHASE 4: RESULT ══════════ */}
        {phase === "result" && result && (
          <div>
            <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>Результат</div>
            <Card testid="mo-recalc-result">
              <div className="text-[18px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                {recipe.title}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4 text-[12.5px]">
                <div>
                  <Label>Было</Label>
                  <div style={{ color: "#2A1D14" }} data-testid="mo-recalc-result-from">{shapeSummary(fromShape)}</div>
                </div>
                <div>
                  <Label>Стало</Label>
                  <div style={{ color: "#2A1D14" }} data-testid="mo-recalc-result-to">{shapeSummary(toShape)}</div>
                </div>
              </div>
              <Label>Пересчитанные ингредиенты</Label>
              {result.renormNote && (
                <div className="text-[11.5px] mb-2 rounded-md px-2 py-1"
                     style={{ background: "#F7F1E7", color: "#5B4326",
                              boxShadow: "inset 2px 0 0 0 #B26B4A" }}
                     data-testid="mo-recalc-renorm-note">
                  {result.renormNote}
                </div>
              )}
              <div className="space-y-1" data-testid="mo-recalc-result-items">
                {result.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_72px_44px] gap-1.5 items-center py-0.5"
                       data-testid="mo-recalc-result-row">
                    <span className="truncate text-[13.5px] pr-1" style={{ color: "#2A1D14" }}>{it.name}</span>
                    <input type="text" inputMode="decimal" value={it.amount}
                           onChange={(e) => {
                             const raw = e.target.value;
                             const parsed = Number(String(raw).replace(",", "."));
                             const items = result.items.map((row, idx) =>
                               idx === i ? { ...row, amount: Number.isFinite(parsed) ? parsed : raw } : row);
                             // Recompute total on manual edits so the "per form" hint stays honest.
                             const totalGrams = items
                               .filter((r) => r.unit === "g").reduce((s, r) => s + (Number(r.amount) || 0), 0)
                               + items.filter((r) => r.unit === "kg").reduce((s, r) => s + (Number(r.amount) || 0) * 1000, 0);
                             setResult({ ...result, items, totalGrams });
                             setDirty(true);
                           }}
                           className="h-8 px-2 rounded-md bg-white text-base md:text-[13px] outline-none text-right tabular-nums min-w-0"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                           data-testid="mo-recalc-result-amount" />
                    <span className="text-[12px] text-left" style={{ color: "#5B4326" }}>{it.unit}</span>
                  </div>
                ))}
              </div>
              {result.totalGrams > 0 && Number(toShape.count) > 1 && (
                <div className="mt-3 pt-3 border-t border-[#EFE4D0] text-[12.5px]" data-testid="mo-recalc-per-form">
                  <div style={{ color: "#5B4326" }}>
                    Общий вес теста: <b>{fmtNum(result.totalGrams)} г</b> · форм: {toShape.count} · на одну ≈ <b>{fmtNum(result.totalGrams / Number(toShape.count))} г</b>
                  </div>
                </div>
              )}
            </Card>

            <div className="mt-4 flex flex-wrap gap-2">
              <PrimaryBtn onClick={openCostFromResult} testid="mo-recalc-cost">Посчитать себестоимость</PrimaryBtn>
              {!savedEntryId ? (
                <PrimaryBtn onClick={saveToWorkbook} disabled={saving} testid="mo-recalc-wb-save">
                  {saving ? "Сохранение…" : "Сохранить в Рабочую тетрадь"}
                </PrimaryBtn>
              ) : (
                <>
                  <PrimaryBtn onClick={saveChanges} disabled={saving} testid="mo-recalc-wb-save-changes">
                    {saving ? "Сохранение…" : "Сохранить изменения"}
                  </PrimaryBtn>
                  <GhostBtn onClick={saveAsVariant} testid="mo-recalc-wb-save-variant">
                    Сохранить как новый вариант
                  </GhostBtn>
                </>
              )}
              <GhostBtn onClick={() => toast("Скоро — добавление в заказ.")} testid="mo-recalc-add-to-order">Добавить в заказ</GhostBtn>
              <GhostBtn onClick={copyResult} testid="mo-recalc-copy"><Copy size={12} className="inline mr-1" />Скопировать</GhostBtn>
              <button onClick={() => setPhase("review")}
                      className="h-11 px-3 rounded-full text-[12.5px]"
                      style={{ background: "transparent", color: "#8B6F4B" }}
                      data-testid="mo-recalc-edit">Редактировать</button>
            </div>
          </div>
        )}
      </div>

      <RecipeCostDrawer
        open={costOpen}
        onClose={() => setCostOpen(false)}
        recipeTitle={recipe.title}
        items={costItems}
        onSnapshot={setCostSnapshot}
        workbook={{
          savedEntryId,
          saving,
          onSave: saveToWorkbook,
          onSaveChanges: saveChanges,
          onSaveVariant: saveAsVariant,
        }}
      />
    </MOShell>
  );
}
